import { useState, useRef, useLayoutEffect, useEffect } from "react";

const SHIMMER_CSS = `
@keyframes sk-shimmer {
  0%   { background-position: -700px 0; }
  100% { background-position:  700px 0; }
}
.sk-box {
  position: absolute;
  pointer-events: none;
  background: linear-gradient(90deg, #e8e8e8 25%, #d0d0d0 50%, #e8e8e8 75%);
  background-size: 700px 100%;
  animation: sk-shimmer 1.5s infinite linear;
  border-radius: 5px;
  opacity: 0;
  transition: opacity 0.25s ease;
}
.sk-box.sk-visible {
  opacity: 1;
}
@keyframes sk-fade {
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
}
.sk-fade { animation: sk-fade 0.38s ease forwards; }
@keyframes sk-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-6px); }
}
@keyframes sk-dot-pulse {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.4; }
  40%           { transform: scale(1.2); opacity: 1; }
}
@keyframes sk-progress-shimmer {
  0%   { left: -40%; }
  100% { left: 110%; }
}
`;

let cssInjected = false;
function injectCSS() {
  if (cssInjected) return;
  const tag = document.createElement("style");
  tag.textContent = SHIMMER_CSS;
  document.head.appendChild(tag);
  cssInjected = true;
}

const MEDIA_TAGS  = new Set(["IMG", "VIDEO", "CANVAS"]);
const INLINE_TAGS = new Set(["BUTTON", "INPUT", "SELECT", "LABEL", "A", "TEXTAREA"]);
const SKIP_TAGS   = new Set(["SCRIPT", "STYLE", "BR", "HR", "NOSCRIPT"]);

function collectBoxes(root) {
  const rr = root.getBoundingClientRect();
  const boxes = [];
  function walk(el) {
    if (SKIP_TAGS.has(el.tagName)) return;
    const isMedia  = MEDIA_TAGS.has(el.tagName);
    const isInline = INLINE_TAGS.has(el.tagName);
    const hasTxt   = [...el.childNodes].some(
      (n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim().length > 1
    );
    const isLeaf = isMedia || isInline || (hasTxt && el.children.length === 0);
    if (isLeaf) {
      const r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return;
      const cs = getComputedStyle(el);
      const isCircle = parseFloat(cs.borderRadius) >= r.width / 2;
      boxes.push({
        top:    Math.round(r.top  - rr.top),
        left:   Math.round(r.left - rr.left),
        width:  Math.round(r.width),
        height: Math.round((isMedia || isInline) ? r.height : Math.min(r.height, 18)),
        br:     isCircle ? "50%" : "5px",
      });
      return;
    }
    for (const child of el.children) walk(child);
  }
  for (const child of root.children) walk(child);
  boxes.sort((a, b) => a.top !== b.top ? a.top - b.top : a.left - b.left);
  return boxes;
}

function ProgressBar({ loading, color }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (loading) {
      setProgress(0);
      const id = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 92) return prev;
          if (prev < 40) return prev + Math.random() * 8;
          if (prev < 70) return prev + Math.random() * 4;
          return prev + Math.random() * 1.5;
        });
      }, 280);
      return () => clearInterval(id);
    } else {
      setProgress(100);
    }
  }, [loading]);

  return (
    <div style={{ width: 980, height: 7, background: "rgba(0,0,0,0.12)", borderRadius: 10, overflow: "hidden", position: "relative" }}>
      <div style={{ width: `${progress}%`, height: "100%", background: `linear-gradient(90deg, ${color}, ${color}cc)`, borderRadius: 10, transition: "width 0.35s ease" }} />
      <div style={{ position: "absolute", top: 0, left: "-40%", width: "40%", height: "100%", background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.6),transparent)", animation: "sk-progress-shimmer 1.8s infinite" }} />
    </div>
  );
}

/**
 * SkeletonWrapper
 *
 * Props:
 *   loading        {boolean}          Show skeleton when true
 *   children                          Any React subtree
 *   staggerDelay   {number}           ms between each bone appearing (default: 60)
 *   showProgress   {boolean}          Show progress bar below skeleton (default: true)
 *   showIcon       {boolean}          Show icon below skeleton (default: false)
 *   icon           {string|ReactNode} Image URL, React element, or emoji
 *   accentColor    {string}           Accent color (default: "#6366f1")
 *
 * @example
 * <SkeletonWrapper
 *   loading={loading}
 *   showProgress={true}
 *   showIcon={true}
 *   icon="https://api.successguardian.in/media/logo_sg_small.png"
 *   accentColor="#6366f1"
 *   staggerDelay={60}
 * >
 *   <YourComponent />
 * </SkeletonWrapper>
 */
export default function SkeletonWrapper({
  loading,
  children,
  staggerDelay = 60,
  showProgress = true,
  showIcon     = false,
  icon         = null,
  accentColor  = "#6366f1",
}) {
  injectCSS();

  const wrapperRef = useRef(null);
  const [boxes, setBoxes]               = useState([]);
  const [visibleCount, setVisibleCount] = useState(0);

  useLayoutEffect(() => {
    if (!loading || !wrapperRef.current) return;
    const next = collectBoxes(wrapperRef.current);
    const key  = JSON.stringify(next);
    setBoxes((prev) => JSON.stringify(prev) === key ? prev : next);
  });

  useEffect(() => {
    if (!loading || boxes.length === 0) return;
    setVisibleCount(0);
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setVisibleCount(i);
      if (i >= boxes.length) clearInterval(id);
    }, staggerDelay);
    return () => clearInterval(id);
  }, [loading, boxes, staggerDelay]);

  const renderFooter = loading && (showProgress || (showIcon && icon));

  // ── When not loading: render children with zero wrapper interference.
  // The sk-fade div is intentionally display:contents so it doesn't
  // introduce a new block/flex context or affect positioning.
  if (!loading) {
    return (
      <div className="sk-fade" style={{ display: "contents" }}>
        {children}
      </div>
    );
  }

  // ── When loading: we need the two wrapper divs for measurement and overlay,
  // so we accept the layout impact only while the skeleton is actually shown.
  // `wrapperStyle` lets the consumer control the display if needed.
  return (
    <div style={{ position: "relative" }}>

      {/* Ref target for bone measurement */}
      <div ref={wrapperRef} style={{ position: "relative" }}>
        {/* Hide children visually but keep them in the DOM so we can measure them */}
        <div style={{ visibility: "hidden" }}>
          {children}
        </div>

        {/* Skeleton bones */}
        {boxes.map((b, i) => (
          <div
            key={i}
            className={`sk-box${i < visibleCount ? " sk-visible" : ""}`}
            style={{ top: b.top, left: b.left, width: b.width, height: b.height, borderRadius: b.br }}
          />
        ))}
      </div>

      {/* Floating icon + progress bar centred over the skeleton */}
      {renderFooter && (
        <div
          style={{
            position:      "absolute",
            top:           "50%",
            left:          "50%",
            transform:     "translate(-50%, -50%)",
            display:       "flex",
            flexDirection: "column",
            alignItems:    "center",
            gap:           12,
            zIndex:        10,
            pointerEvents: "none",
          }}
        >
          {showIcon && icon && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <div style={{ animation: "sk-float 3s ease-in-out infinite" }}>
                {typeof icon === "string" ? (
                  <img src={icon} style={{ height: 100, width: 100, objectFit: "contain" }} alt="" />
                ) : (
                  <div style={{
                    width: 100, height: 100, borderRadius: 12,
                    background: `${accentColor}22`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 52,
                  }}>
                    {icon}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 5 }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: accentColor,
                    animation: "sk-dot-pulse 1.4s infinite",
                    animationDelay: `${i * 0.2}s`,
                  }} />
                ))}
              </div>
            </div>
          )}

          {showProgress && (
            <ProgressBar loading={loading} color={accentColor} />
          )}
        </div>
      )}

    </div>
  );
}